import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function PublicForm() {
  const { code } = useParams();
  const [sp] = useSearchParams();
  const clickId = sp.get("c");
  const [form, setForm] = useState(null);
  const [vals, setVals] = useState({});
  const [state, setState] = useState("loading"); // loading/ready/error/submitting/done
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [tMobile, setTMobile] = useState("");
  const [tRes, setTRes] = useState(null);
  const [tLoad, setTLoad] = useState(false);
  const [showTrack, setShowTrack] = useState(false);
  const checkStatus = async () => {
    if (!tMobile.trim()) return;
    setTLoad(true);
    try { const { data } = await api.post("/public/lead-status", { mobile: tMobile.trim() }); setTRes(data); }
    catch { setTRes({ found: false, tracked: false, message: "Could not check status. Please try again." }); }
    finally { setTLoad(false); }
  };

  useEffect(() => {
    api.get(`/public/form/${code}`).then(({ data }) => {
      setForm(data); setState("ready");
      document.documentElement.style.setProperty("--brand", data.branding?.primary_color || "#10b981");
    }).catch((e) => { setErr(apiErr(e)); setState("error"); });
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    try {
      const { data } = await api.post(`/public/form/${code}/submit`, { responses: vals, click_id: clickId });
      setResult(data); setState("done");
      if (data.redirect_url) setTimeout(() => { window.location.href = data.redirect_url; }, 2500);
    } catch (e2) { setErr(apiErr(e2)); setState("ready"); }
  };

  const brand = form?.branding || {};
  const color = brand.primary_color || "#10b981";

  if (state === "loading") return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" /></div>;
  if (state === "error") return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center text-slate-600">{err}</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 px-4 py-10">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-lg">
        <div className="mb-3 flex items-center justify-center gap-2">
          {brand.logo ? <img src={brand.logo} alt="" className="h-9" /> : <div className="h-8 w-8 rounded-lg" style={{ background: color }} />}
          <span className="font-head text-xl font-bold text-slate-800">{brand.brand_name}</span>
        </div>
        <div className="mb-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <button onClick={() => setShowTrack(!showTrack)} data-testid="track-toggle" className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium text-slate-700">
            <span>Already applied? Track your application</span>
            <span className="text-slate-400">{showTrack ? "−" : "+"}</span>
          </button>
          {showTrack && (
            <div className="border-t border-slate-100 px-4 py-4">
              <div className="flex gap-2">
                <input className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:border-slate-500" placeholder="Enter your mobile number" value={tMobile} onChange={(e) => setTMobile(e.target.value)} data-testid="track-mobile-input" />
                <button onClick={checkStatus} disabled={tLoad} className="rounded-lg px-4 text-sm font-semibold text-white disabled:opacity-60" style={{ background: color }} data-testid="track-check-btn">{tLoad ? "…" : "Check"}</button>
              </div>
              {tRes && (
                <div className={`mt-3 rounded-lg p-3 text-sm ${tRes.tracked ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="track-result">
                  <p className="font-medium">{tRes.message}</p>
                  {tRes.tracked && <p className="mt-1 text-xs text-slate-500">Tracking ID: <span className="font-mono font-semibold">{tRes.tracking_code}</span> · Status: {tRes.conversion_status}</p>}
                  {tRes.tracked && tRes.continue_url && (
                    <a href={tRes.continue_url} data-testid="continue-application-btn" className="mt-2 inline-block rounded-lg px-3 py-1.5 text-xs font-semibold text-white" style={{ background: color }}>Continue Your Application →</a>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="overflow-hidden rounded-2xl bg-white shadow-xl">
          <div className="px-7 py-5" style={{ background: color }}>
            <h1 className="font-head text-xl font-bold text-white">{form.name}</h1>
          </div>
          {state === "done" ? (
            <div className="px-7 py-12 text-center">
              <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color }} />
              <p className="font-head text-lg font-semibold text-slate-800">{result.message}</p>
              <p className="mt-2 text-sm text-slate-500">Reference ID: <span className="font-mono font-semibold" style={{ color }}>{result.tracking_code}</span></p>
              {result.redirect_url && <p className="mt-4 text-xs text-slate-400">Redirecting you to our partner…</p>}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 px-7 py-7">
              {err && <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{err}</div>}
              {form.fields.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-sm font-medium text-slate-700">{f.label}{f.required && <span className="text-rose-500"> *</span>}</label>
                  {f.type === "dropdown" || f.type === "multi_select" ? (
                    <select required={f.required} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-800 outline-none focus:border-slate-500"
                      value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })}>
                      <option value="">Select…</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : f.type === "radio" ? (
                    <div className="flex flex-wrap gap-3">
                      {(f.options || []).map((o) => (
                        <label key={o} className="flex items-center gap-1.5 text-sm text-slate-700">
                          <input type="radio" name={f.key} value={o} checked={vals[f.key] === o} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} /> {o}
                        </label>
                      ))}
                    </div>
                  ) : f.type === "checkbox" ? (
                    <div className="flex flex-wrap gap-3">
                      {(f.options || []).map((o) => {
                        const cur = (vals[f.key] || "").split(", ").filter(Boolean);
                        return (
                          <label key={o} className="flex items-center gap-1.5 text-sm text-slate-700">
                            <input type="checkbox" checked={cur.includes(o)} onChange={(e) => setVals({ ...vals, [f.key]: (e.target.checked ? [...cur, o] : cur.filter((x) => x !== o)).join(", ") })} /> {o}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <input required={f.required} placeholder={f.placeholder || ""}
                      type={f.type === "email" ? "email" : f.type === "date" ? "date" : f.type === "mobile" || f.type === "number" ? "tel" : "text"}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-slate-800 outline-none focus:border-slate-500"
                      value={vals[f.key] || ""} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              <button type="submit" disabled={state === "submitting"} data-testid="public-form-submit"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg py-3 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={{ background: color }}>
                {state === "submitting" ? <Loader2 className="animate-spin" size={18} /> : "Submit Application"}
              </button>
            </form>
          )}
        </div>
        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-slate-400"><ShieldCheck size={13} /> Secured by TrackFlow Pro</p>
      </motion.div>
    </div>
  );
}
